
import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Navigation } from '@/components/Navigation';
import { usePlayerData } from '@/hooks/usePlayerData';
import { PlayerEditData, Player } from '@/types/player';
import { PlayerBreadcrumb } from '@/components/superadmin/player/PlayerBreadcrumb';
import { PlayerHeader } from '@/components/superadmin/player/PlayerHeader';
import { PlayerBasicInfo } from '@/components/superadmin/player/PlayerBasicInfo';
import { PlayerContactInfo } from '@/components/superadmin/player/PlayerContactInfo';
import { PlayerStats } from '@/components/superadmin/player/PlayerStats';
import { PlayerDeleteDialog } from '@/components/superadmin/player/PlayerDeleteDialog';
import { PlayerProgramAssignments } from '@/components/admin/PlayerProgramAssignments';

const SuperAdminPlayer = () => {
  const { playerId } = useParams();
  const { profile, loading, isImpersonating, effectiveRole } = useAuth();
  const { player, playerLoading, updatePlayerMutation, deletePlayerMutation } = usePlayerData(playerId);
  
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [editData, setEditData] = useState<PlayerEditData>({
    position: '',
    jersey_number: '',
    hometown: '',
    height: '',
    grad_year: '',
    dominant_hand: ''
  });

  // Redirect if not superadmin or if currently impersonating
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile || effectiveRole !== 'superadmin' || isImpersonating) {
    return <Navigate to="/" replace />;
  }

  const handleEdit = () => {
    setEditData({
      position: player?.position || 'none',
      jersey_number: player?.jersey_number?.toString() || '',
      hometown: player?.hometown || '',
      height: player?.height || '',
      grad_year: player?.grad_year?.toString() || '',
      dominant_hand: player?.dominant_hand || 'none'
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    const updates = {
      position: editData.position === 'none' ? null : editData.position || null,
      jersey_number: editData.jersey_number ? parseInt(editData.jersey_number) : null,
      hometown: editData.hometown || null,
      height: editData.height || null,
      grad_year: editData.grad_year ? parseInt(editData.grad_year) : null,
      dominant_hand: editData.dominant_hand === 'none' ? null : editData.dominant_hand || null
    };
    
    updatePlayerMutation.mutate(updates);
    setIsEditing(false);
  };

  const handleDelete = () => {
    setDeleteConfirmText('');
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    const playerName = `${player?.profile?.first_name} ${player?.profile?.last_name}`;
    if (deleteConfirmText === playerName) {
      deletePlayerMutation.mutate();
      setIsDeleteDialogOpen(false);
    }
  };

  if (playerLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">Loading player...</div>
        </div>
      </div>
    );
  }

  const playerName = `${player?.profile?.first_name} ${player?.profile?.last_name}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <PlayerBreadcrumb player={player as Player} playerName={playerName} />
        
        <PlayerHeader 
          player={player as Player}
          playerName={playerName}
          isEditing={isEditing}
          isUpdating={updatePlayerMutation.isPending}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancel={() => setIsEditing(false)}
          onDelete={handleDelete}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <PlayerBasicInfo 
            player={player as Player}
            isEditing={isEditing}
            editData={editData}
            onEditDataChange={setEditData}
          />
          <PlayerContactInfo player={player as Player} />
        </div>

        <div className="mt-8">
          <PlayerProgramAssignments playerId={playerId!} teamId={player?.teams?.id} />
        </div>

        <PlayerDeleteDialog
          isOpen={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          playerName={playerName}
          deleteConfirmText={deleteConfirmText}
          onDeleteConfirmTextChange={setDeleteConfirmText}
          onConfirmDelete={handleConfirmDelete}
          isDeleting={deletePlayerMutation.isPending}
        />
      </div>
    </div>
  );
};

export default SuperAdminPlayer;
